'use client';

/**
 * Keeps this tab's active firm in step with identity (spec multi-product 007
 * §2.6 step 1, board unit WP1.24.8): "one membership ⇒ that firm, as today".
 *
 * WHEN IDENTITY CHANGES, THE STORE FOLLOWS IT. Every identity read already
 * carried the tab's claim, so the firm identity answers with is the firm the
 * server admitted for that scope: the claimed one when the person is a member,
 * otherwise the scope rule's. Adopting it means a claim the server no longer
 * admits (a removed membership) is dropped, and a first visit adopts the firm
 * the scope rule picked. A different person resets every scope (the store's
 * own `userId` guard); signing out clears the tab.
 *
 * ADOPTING IS CLAIMING, NOT CHOOSING. The adopted firm is what this tab's
 * requests claim; it is never recorded as the person's last-used firm. For a
 * person in two firms it is the server's pick, and the post-sign-in home must
 * still show the picker (FIX-67; 007 APP-13, PR-11). Only `switchFirm` records
 * a choice.
 *
 * The effect runs on identity CHANGES only, never on the store, so a switch
 * made between two identity reads is not undone before identity refetches.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useAuth } from '@clerk/nextjs';
import { useIdentity } from '@/lib/providers/identity';
import { activeFirmStore } from '@/shared/active-firm';
export const useSyncActiveFirmWithIdentity = () => {
  const $ = _c(6);
  const {
    isLoaded,
    isSignedIn
  } = useAuth();
  const {
    identity,
    identityScope
  } = useIdentity();
  const userId = identity?.userId ?? null;
  const orgId = identity?.orgId ?? null;
  const signedOut = isLoaded && !isSignedIn;
  let t0;
  let t1;
  if ($[0] !== identityScope || $[1] !== orgId || $[2] !== signedOut || $[3] !== userId) {
    t0 = () => {
      if (signedOut) {
        activeFirmStore.getState().clear();
        return;
      }
      if (userId === null || identityScope === null) {
        return;
      }
      activeFirmStore.getState().adoptIdentity(identityScope, {
        userId,
        orgId
      });
    };
    t1 = [signedOut, userId, orgId, identityScope];
    $[0] = identityScope;
    $[1] = orgId;
    $[2] = signedOut;
    $[3] = userId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
};